// Central theme color tokens.
// Keep all project color choices here so future theme switching stays simple.

export const THEME_COLOR_CONFIG = {
  common: {
    black: "#000",
    white: "#fff",
  },
  grey: {
    primary: [
      "#ffffff",
      "#fafafa",
      "#f5f5f5",
      "#f0f0f0",
      "#d9d9d9",
      "#bfbfbf",
      "#8c8c8c",
      "#595959",
      "#262626",
      "#141414",
      "#000000",
    ],
    ascent: ["#fafafa", "#bfbfbf", "#434343", "#1f1f1f"],
    constant: ["#fafafb", "#e6ebf1"],
  },
  breakpoints: {
    xs: 0,
    sm: 768,
    md: 1024,
    lg: 1266,
    xl: 1440,
  },
  paletteMap: {
    primary: "blue",
    error: "red",
    warning: "gold",
    info: "cyan",
    success: "green",
  },
  contrastText: "#fff",
  salifert: {
    trends: {
      linePalette: ["#13c2c2", "#1677ff", "#faad14", "#eb2f96", "#a0d911", "#9254de", "#fa8c16", "#2f54eb", "#52c41a", "#f5222d"],
      tooltipText: "#f5f5f5",
      tooltipBackground: "#1f1f1f",
    },
    trendList: {
      baseBgAlphaLight: 0,
      baseBgAlphaDark: 0.22,
      selectedBgAlphaLight: 0.08,
      selectedBgAlphaDark: 0.22,
      hoverBgAlphaLight: 0.04,
      hoverBgAlphaDark: 0.16,
      selectedBorderAlphaLight: 0.55,
      selectedBorderAlphaDark: 0.88,
    },
    trendGraph: {
      textLight: "#000000",
      textDark: "#e6ebf5",
      secondaryTextDark: "#d7deea",
      gridStrokeLight: "#c5ccd6",
      gridStrokeDark: "#d9dee7",
      gridOpacityLight: 0.55,
      gridOpacityDark: 0.22,
      cursorStrokeOpacity: 0.9,
      cursorStrokeWidth: 1.4,
    },
  },
  selection: {
    baseBgAlphaLight: 0,
    baseBgAlphaDark: 0.22,
    hoverBgAlphaLight: 0.04,
    hoverBgAlphaDark: 0.16,
    selectedBorderAlphaLight: 0.55,
    selectedBorderAlphaDark: 0.88,
  },
  /** Live sensor cards + history charts on Readings (Pixolab / Zima). */
  readings: {
    sensors: {
      tds: '#13c2c2',
      ph: '#1677ff',
      temperature: '#fa8c16',
      ato: '#52c41a',
      ec: '#9254de',
    },
    chart: {
      textLight: '#262626',
      textDark: '#e6ebf5',
      gridLight: '#d9d9d9',
      gridDark: '#434343',
    },
  },
  /**
   * Bordered panels, tables, and cards — matches Devices / Readings data table.
   * Use via `utils/surfaceTheme` (`useSurfaceTheme`, `surfaceTableSx`, etc.).
   */
  surface: {
    borderAlpha: {
      /** Table frames, header rules, cell dividers (Devices data tab). */
      table: { light: 0.22, dark: 0.35 },
      /** MainCard, panel shells, section boxes. */
      panel: { light: 0.14, dark: 0.22 },
      /** Lighter separators and nested outlines. */
      subtle: { light: 0.1, dark: 0.16 },
    },
    panelBgAlpha: {
      /** Accent panels (e.g. live readings column). */
      accent: { light: 0.03, dark: 0.08 },
      /** Muted panels; light mode uses `background.paper` when alpha is 0. */
      muted: { light: 0, dark: 0.05 },
    },
    table: {
      headBorderWidthPx: 2,
      headerBackground: 'transparent',
      bodyBackground: 'transparent',
      /** Light-mode odd row tint (even rows use `background.paper`). */
      zebraOddAlphaLight: 0.03,
      zebraDark: {
        odd: '#4A4A4E',
        even: '#333438',
      },
    },
    frame: {
      borderRadius: 1,
    },
    splitSeparator: {
      alpha: 0.22,
      hoverAlpha: 0.42,
    },
  },
};

/** Primary hue keys — must exist on `@ant-design/colors` presetPalettes. */
export const THEME_PRESETS = [
  { id: "default", label: "Default", key: "blue", swatch: "#1677FF" },
  { id: "theme1", label: "Theme 1", key: "geekblue", swatch: "#2F54EB" },
  { id: "theme2", label: "Theme 2", key: "purple", swatch: "#722ED1" },
  { id: "theme3", label: "Theme 3", key: "cyan", swatch: "#13C2C2" },
  { id: "theme4", label: "Theme 4", key: "green", swatch: "#52C41A" },
  { id: "theme5", label: "Theme 5", key: "magenta", swatch: "#EB2F96" },
  { id: "theme6", label: "Theme 6", key: "orange", swatch: "#FA8C16" },
  { id: "theme7", label: "Theme 7", key: "volcano", swatch: "#FA541C" },
  { id: "theme8", label: "Theme 8", key: "lime", swatch: "#A0D911" },
];

export const THEME_FONT_OPTIONS = [
  {
    id: "public-sans",
    label: "Public Sans",
    stack: `'Public Sans', sans-serif`,
  },
  { id: "inter", label: "Inter", stack: `'Inter', system-ui, sans-serif` },
  {
    id: "roboto",
    label: "Roboto",
    stack: `'Roboto', 'Helvetica', 'Arial', sans-serif`,
  },
  {
    id: "poppins",
    label: "Poppins",
    stack: `'Poppins', system-ui, sans-serif`,
  },
];
